import React, { useEffect } from 'react';

import Icon from 'views/site/Icon.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Modal
 *
 * The shared sheet: scrim, centred card, close control, escape to dismiss, and
 * a body-scroll lock while it is open.
 *
 * This exists because the support centre added two more modals to a page that
 * already had three, and each one had reimplemented the same overlay, the same
 * escape handler and the same transform. Anything new should use this rather
 * than grow a sixth copy.
 *
 * Closed rather than unmounted: `visibility: hidden` keeps it out of the tab
 * order and away from screen readers while letting the transition play, and
 * lets a caller keep state alive across an open/close if it wants to. Callers
 * that want a fresh start each time simply render their body only when open.
 */

const Modal = ({ actions, children, dismissible = true, eyebrow, label, onClose, open, subtitle, title, width = 720 }) => {

    const mobile = Utils.isMobile();

    /*
     * `dismissible: false` is for a modal that exists to collect an answer the
     * visitor cannot skip - the privacy prompt the app hands over, where the
     * actions in the footer are the only exits. Escape, the scrim and the close
     * control all go away together, because leaving one of them wired while
     * hiding the others produces a dialog that closes in a way nobody can see.
     */
    useEffect(() => {
        if(!open || dismissible === false) {
            return;
        }
        const onKeyDown = evt => evt.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [dismissible, open, onClose]);

    useEffect(() => {
        document.body.style.overflowY = open ? 'hidden' : 'scroll';
        return () => { document.body.style.overflowY = 'scroll'; };
    }, [open]);

    return (
        <>
            <div
            className={`site-overlay ${open ? 'active' : ''}`}
            onClick={dismissible === false ? undefined : onClose}
            style={{ zIndex: 6499 }} />

            <div
            aria-hidden={open === false}
            aria-label={label || title || undefined}
            className={'site'}
            role={'dialog'}
            style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                padding: mobile ? 12 : 20,
                pointerEvents: open ? 'all' : 'none',
                position: 'fixed',
                right: 0,
                top: 0,
                visibility: open ? 'visible' : 'hidden',
                zIndex: 6500
            }}>
                <div
                onClick={evt => evt.stopPropagation()}
                style={{
                    backgroundColor: Theme.colors.white,
                    borderRadius: 24,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    maxWidth: width,
                    /*
                     * Without this the corners are only rounded in theory. The
                     * header carries a bottom border and the body has its own
                     * background, and both are square children painting right
                     * up to the edge, so the radius reads as a hairline that
                     * fails to close at the top of the content and at the two
                     * bottom corners.
                     */
                    overflow: 'hidden',
                    opacity: open ? 1 : 0,
                    position: 'relative',
                    transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    width: '100%'
                }}>
                    {/* The header does not scroll — on a long article the way
                        out has to stay reachable without scrolling back up.

                        With `actions` and no `title` it is a bar rather than a
                        heading: the content below supplies its own heading, and
                        a generic one above it reads as two titles stacked. */}
                    <div style={{
                        alignItems: 'center',
                        borderBottom: `1px solid ${Theme.colors.border()}`,
                        display: actions ? 'flex' : 'block',
                        flexShrink: 0,
                        gap: 16,
                        justifyContent: 'space-between',
                        /* the right inset exists to clear the close control.
                           without one it is just a hole the title sits away
                           from, so it comes back to the normal gutter. */
                        padding: actions
                            ? (mobile ? `12px ${dismissible === false ? 16 : 60}px 12px 16px` : `14px ${dismissible === false ? 24 : 76}px 14px 24px`)
                            : (mobile ? `22px ${dismissible === false ? 22 : 60}px 18px 22px` : `26px ${dismissible === false ? 32 : 76}px 22px 32px`)
                    }}>
                        {actions}
                        {eyebrow && (
                            <span style={{
                                ...Theme.textStyles.label(),
                                display: 'block',
                                marginBottom: 8
                            }}>{eyebrow}</span>
                        )}
                        {title && (
                            <span style={{
                                ...Theme.textStyles.heading({ fontSize: mobile ? 21 : 25 }),
                                display: 'block'
                            }}>{title}</span>
                        )}
                        {subtitle && (
                            <span style={{
                                ...Theme.textStyles.body({ fontSize: 14 }),
                                display: 'block',
                                marginTop: 8
                            }}>{subtitle}</span>
                        )}

                        {dismissible !== false && (
                        <button
                        aria-label={'Close'}
                        onClick={onClose}
                        style={{
                            alignItems: 'center',
                            backgroundColor: Theme.colors.offWhite,
                            border: 'none',
                            borderRadius: 18,
                            cursor: 'pointer',
                            display: 'flex',
                            height: 34,
                            justifyContent: 'center',
                            position: 'absolute',
                            right: mobile ? 14 : 20,
                            top: 16,
                            width: 34
                        }}>
                            <Icon color={Theme.colors.darkGrey} name={'x-lg'} size={14} />
                        </button>
                        )}
                    </div>

                    <div style={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        padding: mobile ? 22 : 32
                    }}>
                        {children}
                    </div>
                </div>
            </div>
        </>
    )
}

export default Modal;
